import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './Chat.css';

interface Message {
  id: string;
  senderId: string;
  senderName: string;
  senderAvatar?: string;
  content: string;
  timestamp: string;
  isMe: boolean;
}

interface ChatProps {
  recipientId: string;
  recipientName: string;
  recipientAvatar?: string;
  recipientRole?: string;
  companyName?: string;
  darkMode?: boolean;
  onClose: () => void;
}

const Chat: React.FC<ChatProps> = ({
  recipientId,
  recipientName,
  recipientAvatar,
  recipientRole,
  companyName,
  darkMode = false,
  onClose
}) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load mock conversation history
  useEffect(() => {
    // Simulate loading previous messages
    setMessages([
      {
        id: '1',
        senderId: recipientId,
        senderName: recipientName,
        senderAvatar: recipientAvatar,
        content: 'Hi! I saw your referral request for our company. I\'d be happy to discuss the opportunity with you.',
        timestamp: new Date(Date.now() - 3600000).toISOString(),
        isMe: false
      },
      {
        id: '2',
        senderId: user?.id || '0',
        senderName: user?.name || 'Me',
        senderAvatar: user?.avatar,
        content: 'Thank you for reaching out! I\'m very interested in learning more about the position and the team.',
        timestamp: new Date(Date.now() - 3000000).toISOString(),
        isMe: true
      },
      {
        id: '3',
        senderId: recipientId,
        senderName: recipientName,
        senderAvatar: recipientAvatar,
        content: 'Great! Our team is looking for someone with your skillset. Can you tell me more about your experience with React and TypeScript?',
        timestamp: new Date(Date.now() - 1800000).toISOString(),
        isMe: false
      }
    ]);
  }, [recipientId, recipientName, recipientAvatar, user]);

  const handleSendMessage = () => {
    if (!inputMessage.trim()) return;

    const newMessage: Message = {
      id: Date.now().toString(),
      senderId: user?.id || '0',
      senderName: user?.name || 'Me',
      senderAvatar: user?.avatar,
      content: inputMessage,
      timestamp: new Date().toISOString(),
      isMe: true
    };

    setMessages(prev => [...prev, newMessage]);
    setInputMessage('');
    inputRef.current?.focus();

    // Simulate typing indicator and response
    setTimeout(() => setIsTyping(true), 1000);
    setTimeout(() => {
      setIsTyping(false);
      const responses = [
        'That sounds great! I\'ll forward your information to our hiring manager.',
        'Your experience looks impressive. Would you be available for a quick call this week?',
        'I think you\'d be a great fit for our team. Let me check with HR about next steps.',
        'Thanks for sharing that. I\'ll make sure to highlight your strengths in my referral.'
      ];
      const randomResponse = responses[Math.floor(Math.random() * responses.length)];
      
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        senderId: recipientId,
        senderName: recipientName,
        senderAvatar: recipientAvatar,
        content: randomResponse,
        timestamp: new Date().toISOString(),
        isMe: false
      }]);
    }, 3000);
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className={`chat-container ${darkMode ? 'dark' : 'light'}`}>
      <div className="chat-header">
        <div className="chat-recipient-info">
          {recipientAvatar ? (
            <img src={recipientAvatar} alt={recipientName} className="chat-avatar" />
          ) : (
            <div className="chat-avatar-placeholder">
              {recipientName.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="chat-recipient-details">
            <h3>{recipientName}</h3>
            {recipientRole && companyName && (
              <p>{recipientRole} at {companyName}</p>
            )}
          </div>
        </div>
        <button className="chat-close-btn" onClick={onClose} aria-label="Close chat">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>

      <div className="chat-messages">
        {messages.map(message => (
          <div key={message.id} className={`message ${message.isMe ? 'me' : 'other'}`}>
            {!message.isMe && (
              <div className="message-avatar">
                {message.senderAvatar ? (
                  <img src={message.senderAvatar} alt={message.senderName} />
                ) : (
                  <div className="avatar-placeholder">
                    {message.senderName.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
            )}
            <div className="message-content">
              <div className="message-bubble">
                {message.content}
              </div>
              <div className="message-time">
                {formatTime(message.timestamp)}
              </div>
            </div>
          </div>
        ))}
        {isTyping && (
          <div className="message other">
            <div className="message-avatar">
              {recipientAvatar ? (
                <img src={recipientAvatar} alt={recipientName} />
              ) : (
                <div className="avatar-placeholder">
                  {recipientName.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <div className="message-content">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-container">
        <input
          ref={inputRef}
          type="text"
          className="chat-input"
          placeholder="Type a message..."
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleSendMessage();
            }
          }}
        />
        <button 
          className="chat-send-btn"
          onClick={handleSendMessage}
          disabled={!inputMessage.trim()}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M2 10l16-8-7 16-2-6-7-2z" fill="currentColor"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Chat;